/**
 * Author — 블로그/뉴스 저자 데이터 캐시 (단일 출처)
 *
 * 저자는 `authors` 카테고리의 WP 포스트(post_name = 저자 slug) + ACF `author` 그룹이다.
 * 프로필(이름·직책·소개·강조)과 저자별 포스트 요약 목록을 **하나의 캐시**
 * (`getAuthor-{slug}`)에 담는다 — 상세 페이지·저자별 목록 페이지가 모두 이 캐시를
 * 소비하므로, 저자 수정은 "캐시 1건 재생성 + revalidateTag 1개"로 그 저자의 모든
 * 페이지에 반영된다(글별 상세 캐시 재생성 없음).
 *
 * posts 는 이미 캐시된 블로그/뉴스 리스트(S3)에서 `authorId === slug` 로 필터해
 * **캐시 생성 시점에** 로케일(ko/en)·카테고리(blog/news)별로 분리해 박제한다
 * (라이브 조회 없음). 따라서 이 캐시는 리스트 캐시의 파생물이며, 반드시 리스트 캐시
 * 갱신 **후에** 재생성해야 한다(웹훅·init 파이프라인 순서 주의).
 *
 * 프로필 조회는 단건 post(id:) 대신 **커넥션 형태**를 쓴다 — 단건 조회는 운영 반영
 * 필터(X-Prod-Deployed-Filter, PHP 쿼리 필터)가 타지 않을 수 있고, 카테고리 스코프
 * 없이 slug 만으로 조회하면 다른 카테고리의 동일 slug 글을 잡는다(카테고리 간 slug
 * 중복은 실재). 저자도 prod_deployed 대상이므로 운영 refresh 는 prodFilter 를 넘긴다.
 */

import { CONTENT_CATEGORY_ID } from '@/constants/content-categoryId';
import {
  splitPostSummariesByLocale,
  type PostSummariesByLocale,
  type PostSummary,
} from '@/utils/post-summary';
import { fetchFromCache, FetchAPIOptions } from './api';
import {
  getBlogNewsPostsForList,
  type BlogNewsListItemProp,
} from './blog-news';
import { fetchFromWordpress } from './wordpress-api';
import { uploadWordpressCacheDataToS3 } from './wordpress-cache';

// ============================================
// Types
// ============================================

/** 카테고리별 저자 포스트 요약 — 소비처는 자기 카테고리 배열만 읽는다 */
export interface AuthorPosts {
  blog: PostSummary[];
  news: PostSummary[];
}

/**
 * 로케일 한 쪽의 저자 데이터 — 페이지는 자기 locale 것만 꺼내 쓴다.
 * position·isFeatured 는 상세 저자 섹션 UI 개편이 소비할 필드 — 캐시 스키마 변경은
 * 전체 재생성+재배포를 요구하므로 선제 포함한다.
 */
export interface AuthorLocaleData {
  position: string;
  description: string;
  posts: AuthorPosts;
}

/** 저자별 캐시 데이터 — 프로필 공통 필드 + 로케일별 그룹 (태그 캐시와 동일 패턴) */
export interface AuthorData {
  slug: string;
  name: string;
  image: string;
  isFeatured: boolean;
  lang: {
    ko: AuthorLocaleData;
    en: AuthorLocaleData;
  };
}

/** 프로필 쿼리 응답 노드 (느슨한 형태 — 필드 누락을 허용하고 조립에서 기본값 처리) */
interface AuthorProfileNode {
  featuredImage?: { node?: { sourceUrl?: string } } | null;
  author?: {
    id?: string;
    name?: string;
    descriptionEn?: string;
    descriptionKo?: string;
    isFeatured?: boolean;
    positionEn?: string;
    positionKo?: string;
  } | null;
}

// ============================================
// 상수
// ============================================

/** 캐시 폴더명이자 revalidateTag 태그 — content-cache-handlers 의 저자 무효화도 이 헬퍼를 쓴다 */
export const getAuthorCacheLabel = (slug: string) => `getAuthor-${slug}`;

// 저자 프로필 조회 — 저자 slug(= post_name)로 authors 카테고리 내에서 ACF author
// 그룹 + 프로필 이미지(featured image)를 조회한다. 카테고리 스코프는 categoryId 로
// 건다 — authors 는 term_id 가 dev/운영 동일(10)해 환경 분기가 없고, WP 카테고리
// slug('author', 단수)가 내부 라벨('authors')과 달라 categoryName 은 오히려 함정이다.
const AUTHOR_PROFILE_QUERY = `
query getAuthorProfile($slugs: [String], $categoryId: Int) {
  posts(first: 1, where: { nameIn: $slugs, categoryId: $categoryId, status: PUBLISH }) {
    nodes {
      featuredImage {
        node {
          sourceUrl
        }
      }
      author {
        id
        name
        descriptionEn
        descriptionKo
        isFeatured
        positionEn
        positionKo
      }
    }
  }
}`;

// ============================================
// 순수 함수 (조립)
// ============================================

/**
 * 후보 리스트에서 해당 저자의 글을 골라 로케일별 요약 목록으로 만든다.
 * 후보 한 카테고리(블로그 또는 뉴스)에 대해 호출한다. date 내림차순(최신순) —
 * 기존 저자 페이지의 "목록 필터 + date desc" 결과와 동일한 집합·순서를 보장해야 한다.
 */
export const pickAuthorPosts = (
  slug: string,
  candidates: BlogNewsListItemProp[],
): PostSummariesByLocale =>
  splitPostSummariesByLocale(candidates.filter(post => post.authorId === slug));

/**
 * 프로필 노드 + 카테고리별 로케일 posts → AuthorData 조립.
 * slug 는 요청 slug(= ACF `author.id` = post_name)를 그대로 쓴다 — GraphQL relay ID 는
 * 링크·필터에 쓰는 authorId 와 의미가 다르다.
 */
export const assembleAuthorData = (
  slug: string,
  profile: AuthorProfileNode,
  posts: { blog: PostSummariesByLocale; news: PostSummariesByLocale },
): AuthorData => ({
  slug,
  name: profile.author?.name ?? '',
  image: profile.featuredImage?.node?.sourceUrl ?? '',
  isFeatured: profile.author?.isFeatured ?? false,
  lang: {
    ko: {
      position: profile.author?.positionKo ?? '',
      description: profile.author?.descriptionKo ?? '',
      posts: { blog: posts.blog.ko, news: posts.news.ko },
    },
    en: {
      position: profile.author?.positionEn ?? '',
      description: profile.author?.descriptionEn ?? '',
      posts: { blog: posts.blog.en, news: posts.news.en },
    },
  },
});

// ============================================
// 캐시 형태 검증
// ============================================

const isAuthorLocaleData = (value: unknown): value is AuthorLocaleData => {
  if (typeof value !== 'object' || value === null) return false;

  const data = value as Partial<AuthorLocaleData>;
  return (
    typeof data.position === 'string' &&
    typeof data.description === 'string' &&
    typeof data.posts === 'object' &&
    data.posts !== null &&
    Array.isArray((data.posts as Partial<AuthorPosts>).blog) &&
    Array.isArray((data.posts as Partial<AuthorPosts>).news)
  );
};

/**
 * 캐시 형태 검증 — 구 스키마(프로필 단건, `lang` 그룹 없음)나 오염된 캐시가 조용히
 * 통과하지 않도록 전 필드를 검사한다. 불일치 시 소비처는 캐시 미스로 폴백한다.
 */
export const isAuthorData = (value: unknown): value is AuthorData => {
  if (typeof value !== 'object' || value === null) return false;

  const data = value as Partial<AuthorData>;
  return (
    typeof data.slug === 'string' &&
    typeof data.name === 'string' &&
    typeof data.image === 'string' &&
    typeof data.isFeatured === 'boolean' &&
    typeof data.lang === 'object' &&
    data.lang !== null &&
    isAuthorLocaleData(data.lang.ko) &&
    isAuthorLocaleData(data.lang.en)
  );
};

// ============================================
// 캐시 데이터 빌드
// ============================================

/** 후보 리스트 주입 형태 — 배치(init)가 리스트를 1회만 읽어 전 저자에 재사용한다 */
export interface AuthorPostCandidates {
  blog: BlogNewsListItemProp[];
  news: BlogNewsListItemProp[];
}

type BuildAuthorOptions = Pick<FetchAPIOptions, 'prodFilter'> & {
  /** 생략하면 내부에서 블로그/뉴스 리스트 캐시를 각 1회 조회한다 */
  candidates?: AuthorPostCandidates;
};

/**
 * 프로필 라이브 조회 + 캐시된 블로그/뉴스 리스트 필터 → AuthorData.
 * (S3 업로드 없음 — 검증/dry-run 용으로도 사용)
 *
 * - 저자 포스트가 없으면(미존재 또는 prodFilter 제외) null — 캐시를 만들지 않는다
 * - 리스트가 비어 있으면 throw — 리스트 조회 실패가 "posts 0개인 유효한 캐시"로
 *   조용히 구워지는 것을 막는다(블로그·뉴스 모두 글이 0개인 상태는 실재하지 않는다)
 */
export const buildAuthorData = async (
  slug: string,
  options?: BuildAuthorOptions,
): Promise<AuthorData | null> => {
  const [profileData, blogList, newsList] = await Promise.all([
    fetchFromWordpress(
      AUTHOR_PROFILE_QUERY,
      {
        variables: {
          slugs: [slug],
          categoryId: CONTENT_CATEGORY_ID.authors,
        },
      },
      { prodFilter: options?.prodFilter },
    ),
    options?.candidates?.blog ??
      getBlogNewsPostsForList(CONTENT_CATEGORY_ID.blog),
    options?.candidates?.news ??
      getBlogNewsPostsForList(CONTENT_CATEGORY_ID.news),
  ]);

  const node: AuthorProfileNode | undefined = profileData?.posts?.nodes?.[0];
  if (!node) return null;

  if (blogList.length === 0 || newsList.length === 0) {
    throw new Error(
      `저자 캐시 빌드 실패(${slug}): 블로그/뉴스 리스트 캐시가 비어 있습니다 — 리스트 캐시를 먼저 재생성하세요`,
    );
  }

  return assembleAuthorData(slug, node, {
    blog: pickAuthorPosts(slug, blogList),
    news: pickAuthorPosts(slug, newsList),
  });
};

// ============================================
// 조회 (빌드/런타임 + refresh)
// ============================================

/**
 * 저자 데이터 조회
 * - refresh: true → 프로필 조회 + 리스트 필터 + S3 업로드 (스크립트/웹훅용, 실패 시 throw).
 *   저자 미존재(또는 prodFilter 제외) 시 null — 기존 캐시는 건드리지 않는다
 * - refresh: false → S3 캐시 조회 (빌드/런타임용, 미스·형태 불일치 시 null)
 */
export const getAuthorData = async (
  slug: string,
  options?: FetchAPIOptions & { candidates?: AuthorPostCandidates },
): Promise<AuthorData | null> => {
  const refresh = options?.refresh ?? false;
  const label = getAuthorCacheLabel(slug);

  if (refresh) {
    const data = await buildAuthorData(slug, {
      prodFilter: options?.prodFilter,
      candidates: options?.candidates,
    });
    if (!data) return null;

    await uploadWordpressCacheDataToS3(data, label);
    return data;
  }

  const cached = await fetchFromCache(label);
  return isAuthorData(cached) ? cached : null;
};

// ============================================
// 포스트 저장 커플링 — 이전∪현재 저자 재생성용
// ============================================

/**
 * 포스트 저장·삭제 시 재생성해야 할 저자 slug 목록을 정한다.
 * 이전 저자 ∪ 현재 저자(중복·빈 값 제외) — 저자가 바뀌었거나 글이 내려가도 이전 저자의
 * posts 에서 빠지도록 이전 값을 포함한다.
 */
export const authorsToRefreshForPost = (
  oldAuthorId: string | null,
  newAuthorId: string | null,
): string[] =>
  Array.from(new Set([oldAuthorId, newAuthorId].filter(Boolean) as string[]));
